// The GitHostClient seam (contracts.md registry row "GitHostClient seam"; ADR-0016 §5; D12).
//
// GitHub App and GitLab OAuth normalized behind ONE ExternalClient interface. Least-privilege by
// construction: the permission set NEVER includes administration (SETUP.md:17). Installation tokens
// are short-lived and NEVER persisted. Webhooks are verified both ways (GitHub HMAC, GitLab static
// token) into one typed event. Types + Zod schemas only here; the fixture lands in
// @nightcrew/connections (Wave 2), the real adapter behind `connections.real_git_host`.
import { z } from "zod";
import { RepoHost } from "../model/enums.js";
import { NonEmptyString, Timestamp } from "../model/common.js";
/** The Git host. Alias of the RepoHost closed set (enums.ts) - the seam's vocabulary, one source of truth. */
export const GitHost = RepoHost;
/**
 * The least-privilege permission manifest the install requests. Every capability is pinned to its
 * exact grant so an illegal grant does not parse, and the object is STRICT: an extra key (notably
 * `administration`) is REJECTED at the boundary. Administration is structurally absent - it is not a
 * field of this type and cannot be added at rest.
 */
export const GitHostPermissions = z.strictObject({
    contents: z.literal("write"),
    pull_requests: z.literal("write"),
    issues: z.literal("write"),
    checks: z.literal("write"),
    statuses: z.literal("write"),
    actions: z.literal("write"),
    workflows: z.literal("write"),
    metadata: z.literal("read"),
});
/** A completed installation: the host, the provider install id, the account login, its repos, and the granted scope. */
export const GitHostInstallation = z.strictObject({
    host: GitHost,
    /** The provider's installation id (an opaque string, not a UUID). */
    installationId: NonEmptyString,
    /** The GitHub/GitLab account (org or user) login the app is installed on. */
    accountLogin: NonEmptyString,
    repositoryIds: z.array(z.string()).readonly(),
    permissions: GitHostPermissions,
});
/**
 * A short-lived installation token. NEVER persisted: minted just-in-time for a single runner lease
 * and discarded after use; only `expiresAt` bounds its lifetime (ADR-0016 §5). It is not a column
 * on any table and never appears in a log.
 */
export const InstallationToken = z.strictObject({
    token: NonEmptyString,
    expiresAt: Timestamp,
});
/**
 * The webhook events the seam normalizes across both hosts. Kept minimal (the two revocation-class
 * events plus a repo-set change); widen later as more provider events are handled. A discriminated
 * union so an event that does not match a known kind does not parse.
 */
// `sentAt` (unix seconds) is the delivery time carried INSIDE the signed body; the verifier rejects
// a delivery outside the replay-tolerance window (webhook.ts MAX_WEBHOOK_AGE_SECONDS). Optional so a
// body without it still parses (the check simply does not apply); the fixture always includes it.
const sentAt = z.number().int().optional();
export const GitHostWebhookEvent = z.discriminatedUnion("kind", [
    z.strictObject({
        kind: z.literal("installation_revoked"),
        host: GitHost,
        installationId: NonEmptyString,
        sentAt,
    }),
    z.strictObject({
        kind: z.literal("installation_suspended"),
        host: GitHost,
        installationId: NonEmptyString,
        sentAt,
    }),
    z.strictObject({
        kind: z.literal("repositories_changed"),
        host: GitHost,
        installationId: NonEmptyString,
        repositoryIds: z.array(z.string()).readonly(),
        sentAt,
    }),
]);
//# sourceMappingURL=git-host.js.map